"use client";

import { Box, usePrevious } from "@biom3/react";
import type { Content } from "@prismicio/client";
import {
  Alignment,
  type FileAsset,
  Fit,
  type ImageAsset,
  Layout,
  decodeImage,
  useRive,
  useStateMachineInput,
} from "@rive-app/react-canvas";
import { useCallback, useEffect, useMemo } from "react";

const STATE_MACHINE = "ButtonSM";
const INPUT_LOADING = "Loading";
const INPUT_LOADED = "Loaded";

type RiveFollowButtonProps = {
  isWishlisted: boolean;
  isLoading: boolean;
  onClick: () => void;
  game?: Content.GameDocument;
};

type FollowVariant = "standard" | "paas";

interface FollowButtonConfig {
  variant: FollowVariant;
  imageUrl: string;
}

function getFollowButtonConfig(
  uid: string | undefined,
  isPointsTemplate: boolean,
): FollowButtonConfig {
  switch (uid) {
    case "norma-in-metaland":
      return {
        variant: isPointsTemplate ? "paas" : "standard",
        imageUrl: "/images/follow-button/norma-in-metaland.png",
      };

    case "ravenquest":
      return {
        variant: "standard",
        imageUrl: "/images/follow-button/ravenquest.png",
      };

    default:
      return {
        variant: isPointsTemplate ? "paas" : "standard",
        imageUrl: "",
      };
  }
}

export function RiveFollowButton({
  isWishlisted,
  onClick,
  isLoading,
  game,
}: RiveFollowButtonProps) {
  const isPointsTemplate = useMemo(() => {
    if (!game) return false;
    const { uid } = game;

    return uid === "norma-in-metaland";
  }, [game]);

  const { variant, imageUrl } = useMemo(
    () => getFollowButtonConfig(game?.uid, isPointsTemplate),
    [game?.uid, isPointsTemplate],
  );

  const riveSrc =
    variant === "paas" || game?.uid === "immutable-play-onboarding"
      ? "/rive/follow_paas.riv"
      : "/rive/follow_button.riv";

  const assetLoader = useCallback(
    (asset: FileAsset | ImageAsset) => {
      if (!asset.isImage || !imageUrl) return false;
      fetch(imageUrl)
        .then((res) => res.arrayBuffer())
        .then((buf) => decodeImage(new Uint8Array(buf)))
        .then((img) => {
          (asset as ImageAsset).setRenderImage(img);
          img.unref();
        });
      return true;
    },
    [imageUrl],
  );

  const { rive, RiveComponent } = useRive({
    src: riveSrc,
    autoplay: true,
    layout: new Layout({ fit: Fit.Cover, alignment: Alignment.Center }),
    stateMachines: STATE_MACHINE,
    assetLoader,
    isTouchScrollEnabled: true,
  });

  const loadingInput = useStateMachineInput(
    rive,
    STATE_MACHINE,
    INPUT_LOADING,
    false,
  );
  const loadedInput = useStateMachineInput(
    rive,
    STATE_MACHINE,
    INPUT_LOADED,
    false,
  );

  const previousIsWishlisted = usePrevious(isWishlisted);
  const unfollowed = useMemo(
    () => previousIsWishlisted && !isWishlisted,
    [previousIsWishlisted, isWishlisted],
  );

  useEffect(() => {
    if (!rive || !loadingInput || !loadedInput) return;

    // Example of how to change the follow text and gems text of the button
    rive.setTextRunValue("FollowTextRun", "Join Immutable Play");
    // rive.setTextRunValue("GemsTextRun", "+100");

    if (isLoading) {
      loadingInput.value = true;
      loadedInput.value = false;
      return;
    }

    if (isWishlisted) {
      loadedInput.value = true;
    }

    if (unfollowed) {
      rive.load({ src: riveSrc, autoplay: true, stateMachines: STATE_MACHINE });
    }
  }, [
    isLoading,
    isWishlisted,
    unfollowed,
    rive,
    loadingInput,
    loadedInput,
    riveSrc,
  ]);
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        width: "100%",
        height: "60px",
        mb: "base.spacing.x6",
      }}
    >
      <Box
        rc={
          <RiveComponent
            onClick={() => {
              if (onClick) {
                onClick();
              }
            }}
          />
        }
        sx={{
          width: "500px",
          height: {
            default: "250px",
            small: "200px",
            medium: "250px",
            large: "330px",
            xxxLarge: "375px",
          },
          maxWidth: "500px",
          cursor: "pointer",
        }}
      />
    </Box>
  );
}
